import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Bell,
  Settings,
  LogOut,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/users', icon: Users, label: 'Users' },
  { to: '/subscriptions', icon: CreditCard, label: 'Subscriptions' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
  { to: '/app-settings', icon: Settings, label: 'App Settings' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { logout } = useAuth();

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-16 flex items-center px-6 border-b border-border">
        <span className="text-primary text-xl font-bold">Plus</span>
        <span className="ml-2 bg-primary-light text-primary text-[10px] font-semibold px-2 py-0.5 rounded-full">
          Admin
        </span>
        <button
          onClick={onClose}
          className="ml-auto p-1 rounded-lg hover:bg-surface md:hidden text-text-hint"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 px-3">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-btn mx-1 mb-1 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? 'bg-primary-light text-primary font-semibold'
                  : 'text-text-secondary hover:bg-surface'
              }`
            }
          >
            <item.icon className="w-[18px] h-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between px-2">
          <div>
            <p className="text-sm font-semibold text-text-primary">Admin</p>
            <p className="text-[11px] text-text-hint">Super Admin</p>
          </div>
          <button
            onClick={logout}
            className="p-2 rounded-lg text-text-hint hover:text-error hover:bg-red-50 transition-colors"
            title="Logout"
          >
            <LogOut className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:block fixed left-0 top-0 bottom-0 w-60 bg-white border-r border-border z-30">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={onClose} />
          <aside className="fixed left-0 top-0 bottom-0 w-60 bg-white z-50 md:hidden shadow-xl animate-[slideIn_200ms]">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
